/**
 * External dependencies
 */
import axios from "axios";
import qs from "querystring";

/**
 * WordPress dependencies
 */
import { __ } from "@wordpress/i18n";

/**
 * Internal dependencies
 */
import Popup from "../components/popup";
import {
	createLocationRadio,
	getCoordinates,
	getLocationsHeading,
	loadLocationsByAddress,
	loadLocationsByLatLng,
} from "../utils/locations";
import getPosition from "../utils/get-position";
import calculateAge from "../utils/calculate-age";
import FormValidator from "../utils/form-validator";
import Toast from "../components/toast";

/* global navigator */

const {
	FormData,
	NinetyNineData: {
		ajaxurl,
		registerUserNonce,
		validateReferralCodeNonce,
		validateCardNumberNonce,
	},
} = window;

export default class {
	constructor(block) {
		this.form = block.querySelector("form");
		this.adminbar = document.getElementById("wpadminbar");
		this.siteHeader = document.querySelector("header.site-header");
		this.clearButton = this.form.querySelector(".btn-clear");
		this.submitButton = this.form.querySelector(".submit-button");
		this.genericErrorMessage = this.form.querySelector(".generic-error");
		this.toggleLinks = this.form.querySelectorAll(
			".referral-and-loyalty-title a"
		);

		this.clearButton.addEventListener("click", () => this.form.reset());

		for (const link of this.toggleLinks) {
			link.addEventListener("click", this.toggleHiddenFields.bind(this));
		}

		this.initLocationSearch();
		this.initFormHandler();

		this.validator = new FormValidator(this.form, {
			callback: this.validationCallback.bind(this),
		});

		this.successPopup = new Popup(document.querySelector(".nnr-welcome-popup"));
	}

	validationCallback(field, validator, isValid) {
		if (true === validator.fullFormCheck) {
			return;
		}

		if (!isValid) {
			this.submitButton.disabled = true;
		} else if (this.form.checkValidity()) {
			this.submitButton.disabled = false;
		}
	}

	getOrderLink(authorizationGrant, expiresIn) {
		return this.form
			.getAttribute("data-order-link")
			.replace("{code}", authorizationGrant)
			.replace("{expires_in}", expiresIn);
	}

	initFormHandler() {
		this.passwordField = this.form.querySelector("[name=password]");
		this.postalCodeField = this.form.querySelector("[name=postalCode]");

		if (this.postalCodeField) {
			this.postalCodeField.addEventListener(
				"input",
				this.handlePostalCodeField.bind(this)
			);
		}

		this.cardNumberField = this.form.querySelector("[name=loyalty-card]");
		this.cardNumberButton = this.form.querySelector(".check-loyalty-card");
		this.cardNumberValidInfo = this.form.querySelector(
			".card-number-valid-info"
		);

		this.phoneNumberField = this.form.querySelector("[name=mobilePhone]");
		this.birthdayFields = this.form.querySelectorAll(
			".content-birthday input[type='number']"
		);

		this.form.addEventListener("submit", this.handleSubmit.bind(this));

		if (this.passwordField) {
			this.passwordField.addEventListener(
				"focus",
				this.handlePasswordFieldFocusIn
			);

			this.passwordField.addEventListener(
				"focusout",
				this.handlePasswordFieldFocusOut
			);
		}

		if (this.phoneNumberField) {
			this.phoneNumberField.addEventListener(
				"input",
				this.handlePhoneNumberInput.bind(this)
			);
		}

		if (this.birthdayFields) {
			this.handleBirthdayFieldsInputs();
		}

		if (this.cardNumberField) {
			this.cardNumberField.addEventListener(
				"input",
				this.handleCardNumberInput.bind(this)
			);

			this.cardNumberField.addEventListener(
				"blur",
				this.handleCardNumberBlur.bind(this)
			);

			this.cardNumberButton.addEventListener(
				"click",
				this.validateCardNumber.bind(this)
			);
		}
	}

	handlePostalCodeField(e) {
		if (isNaN(e.target.value)) {
			e.target.value = parseInt(e.target.value) || "";
		}
	}

	handleCardNumberInput() {
		if (!this.cardNumberField.value) {
			this.cardNumberField.notEmpty = false;
			this.cardNumberButton.disabled = true;
			return;
		}

		this.cardNumberButton.disabled = false;
		this.cardNumberField.notEmpty = true;
		this.cardNumberField.hasChanged = true;
	}

	handleCardNumberBlur(e) {
		if (e.target.hasChanged) {
			for (const error in e.target.validity) {
				if ("valid" === error || "notChecked" === error) {
					continue;
				}

				if (true === e.target.validity[error]) {
					e.target.validity.notChecked = false;
					return;
				}
			}

			e.target.setCustomValidity("notChecked");
			e.target.validity.notChecked = true;
		}
	}

	handlePhoneNumberInput(e) {
		const regex = /(\D+)/g;
		let first3 = "";
		let middle3 = "";
		let last4 = "";

		e.target.value = e.target.value.replace(regex, "");

		let position;

		if (3 >= e.target.value.length) {
			position = e.target.value.length + 1;
		} else if (3 < e.target.value.length && 6 >= e.target.value.length) {
			position = e.target.value.length + 3;
		} else {
			position = e.target.value.length + 4;
		}

		first3 = e.target.value.substr(0, 3).padEnd(3, "_");
		middle3 = e.target.value.substr(3, 3).padEnd(3, "_");
		last4 = e.target.value.substr(6, 4).padEnd(4, "_");

		const value = `(${first3})-${middle3}-${last4}`;

		if ("(___)-___-____" === value) {
			e.target.value = "";
		} else {
			e.target.value = value;
			e.target.setSelectionRange(position, position);
		}
	}

	handleBirthdayFieldsInputs() {
		this.yearField = document.querySelector("input[name=year]");

		this.yearField.addEventListener(
			"blur",
			this.handleBirthdayFieldsBlur.bind(this)
		);

		const nextHandler = ({ target }) => {
			const index = parseInt(target.dataset.index);
			target.value = target.value.slice(0, target.maxLength);

			if ("" !== target.value) {
				target.dataset.shouldGoBack = false;
			}

			if (parseInt(target.value) > parseInt(target.max)) {
				target.value = target.value.slice(0, target.value.length - 1);
			}

			if (
				target.checkValidity() &&
				target.value.length === target.maxLength &&
				index < this.birthdayFields.length - 1
			) {
				this.birthdayFields[index + 1].focus();
			}
		};

		const prevHandler = e => {
			if (8 !== e.keyCode) {
				return;
			}

			if ("true" === e.target.dataset.shouldGoBack) {
				e.target.dataset.shouldGoBack = false;
				const index = parseInt(e.target.dataset.index) - 1;

				if (index >= 0) {
					this.birthdayFields[index].focus();
				}
			} else if ("" === e.target.value) {
				e.target.dataset.shouldGoBack = true;
			}
		};

		for (const [index, field] of this.birthdayFields.entries()) {
			field.dataset.index = index;
			field.addEventListener("input", nextHandler);
			field.addEventListener("keyup", prevHandler);
		}
	}

	handlePasswordFieldFocusIn(e) {
		e.target
			.closest(".field-wrap")
			.querySelector("[data-type='patternMismatch']")
			.classList.add("visible-no-error");
	}

	handlePasswordFieldFocusOut(e) {
		e.target
			.closest(".field-wrap")
			.querySelector("[data-type='patternMismatch']")
			.classList.remove("visible-no-error");
	}

	handleBirthdayFieldsBlur(e) {
		if ("month" === document.activeElement.name) {
			return;
		}

		const values = {};

		for (const field of this.birthdayFields) {
			values[field.name] = parseInt(field.value);
		}

		const date = new Date();

		date.setFullYear(values.year, values.month - 1, values.day);

		if (date.getTime() >= new Date().getTime()) {
			this.validator.setError(e.target, "invalidDate");
		} else {
			this.validator.setError(e.target, "invalidDate", false);

			const age = calculateAge(date);

			if (age < 13) {
				this.validator.setError(e.target, "tooYoung");
			} else {
				this.validator.setError(e.target, "tooYoung", false);
			}
		}

		this.validator.validateField(e.target);
	}

	async validateReferralCode(e) {
		e.preventDefault();

		this.setProcessingState(this.referralCodeButton);

		await this.validateNumbers(
			this.referralCodeField,
			this.referralCodeValidInfo,
			{
				action: "nnr_validate_referral_code",
				_wpnonce: validateReferralCodeNonce,
				code: this.referralCodeField.value,
			}
		);

		this.referralCodeButton.classList.remove("is-loading");
	}

	async validateCardNumber(e) {
		e.preventDefault();

		this.setProcessingState(this.cardNumberButton);

		this.cardNumberField.hasChanged = false;
		this.cardNumberField.validity.notChecked = false;

		this.cardNumberField.setCustomValidity("");

		this.validator.validateField(this.cardNumberField);

		await this.validateNumbers(this.cardNumberField, this.cardNumberValidInfo, {
			action: "nnr_validate_card_number",
			_wpnonce: validateCardNumberNonce,
			number: this.cardNumberField.value,
		});

		this.cardNumberButton.classList.remove("is-loading");
	}

	async validateNumbers(field, info, params) {
		info.classList.remove("is-visible");

		const {
			data: { success, data },
		} = await axios({
			url: ajaxurl,
			params,
		});

		if (true === success) {
			info.classList.add("is-visible");
		} else {
			field.setCustomValidity(data.message);
			this.validator.validateField(field, data.message);
		}
	}

	async handleSubmit(e) {
		e.preventDefault();

		if (!this.form.checkValidity()) {
			this.reportErrors();
			return;
		}

		this.genericErrorMessage.classList.remove("is-visible");
		this.setProcessingState(this.submitButton);

		const formData = Object.fromEntries(new FormData(this.form).entries());

		const {
			data: { success, data },
		} = await axios({
			method: "post",
			url: ajaxurl,
			data: qs.stringify({
				action: "nnr_register_user",
				_wpnonce: registerUserNonce,
				...formData,
			}),
		});

		this.setProcessingState(this.submitButton, false);

		if (false === success) {
			if ("validation" === data.errorCode) {
				this.handleValidationErrors(data.errors);
				this.reportErrors();
			} else {
				this.genericErrorMessage.classList.add("is-visible");
			}
		} else {
			this.handleSuccess(data);
		}
	}

	reportErrors() {
		this.submitButton.disabled = true;

		new Toast("error", this.form.dataset.genericError);

		this.scrollToError();
	}

	handleValidationErrors(errors) {
		for (const fieldName in errors) {
			const field = this.form.querySelector(`[name=${fieldName}]`);

			if (field) {
				this.validator.setCustomError(field, errors[fieldName]);
			}
		}
	}

	scrollToError() {
		const firstInvalidField = this.form.querySelector("input:invalid");
		let offset = 0;
		const adminBarOffset =
			this.adminbar &&
			"fixed" === window.getComputedStyle(this.adminbar).position
				? this.adminbar.offsetHeight
				: 0;

		if (!this.siteHeader) {
			offset = adminBarOffset;
		} else {
			offset =
				("fixed" === window.getComputedStyle(this.siteHeader).position
					? this.siteHeader.offsetHeight
					: 0) + adminBarOffset;
		}

		document.documentElement.scrollTop =
			getPosition(firstInvalidField).y - offset;
	}

	handleSuccess({ authorizationGrant, expiresIn }) {
		const url = this.getOrderLink(authorizationGrant, expiresIn);

		const button = this.successPopup.element.querySelector("a.order-button");

		if (button) {
			button.setAttribute("href", url);
		}

		this.form.reset();
		this.successPopup.open();
	}

	toggleHiddenFields(e) {
		e.preventDefault();

		e.target
			.closest(".referral-and-loyalty-item")
			.querySelector(".referral-and-loyalty-fields")
			.classList.add("active");
	}

	initLocationSearch() {
		this.locationDiv = this.form.querySelector(".found-locations");
		this.locationsWrap = this.form.querySelector(".locations-wrap");
		this.locationsLoader = this.form.querySelector(".locations-loader");
		this.noLocationsMessage = this.form.querySelector(".no-locations-message");
		this.locationsError = this.form.querySelector(".locations-error-message");
		this.foundLocationsHeader = this.locationDiv.querySelector(
			".found-locations-header"
		);
		this.locationSearchButton = this.form.querySelector(
			".location-search-button"
		);
		this.locationSearchField = this.form.querySelector(
			".location-search-field"
		);

		this.locationSearchButton.addEventListener(
			"click",
			this.handleLocationSearch.bind(this)
		);

		this.locationSearchField.addEventListener("input", e => {
			if (true !== e.target.locationsLoaded) {
				this.validator.setError(e.target, "noLocations");
			}
		});

		this.locationSearchField.addEventListener(
			"keydown",
			e => 13 === e.keyCode && this.handleLocationSearch(e)
		);

		this.initialLocationSearchDone = false;

		this.locationSearchField.addEventListener("focus", () => {
			if (false === this.initialLocationSearchDone) {
				this.handleInitialLocationSearch();
				this.initialLocationSearchDone = true;
			}
		});
	}

	async handleLocationSearch(e) {
		e.preventDefault();
		this.locationDiv.classList.remove("is-visible");
		this.locationsError.classList.remove("is-visible");
		this.noLocationsMessage.classList.remove("is-visible");

		this.validator.setError(this.locationSearchField, "noLocations", false);

		try {
			await this.performLocationSearch({
				address: this.locationSearchField.value,
			});
		} catch (error) {
			this.displayLocationsError(
				__("Something went wrong. Please try again.", "ocharleys")
			);
		}
	}

	async handleInitialLocationSearch() {
		if (!navigator.geolocation) {
			return;
		}

		try {
			const position = await getCoordinates();

			const { searchstring } = await this.performLocationSearch({
				byAddress: false,
				lat: position.coords.latitude,
				lng: position.coords.longitude,
			});

			this.locationSearchField.value = searchstring;
		} catch (e) {}
	}

	async performLocationSearch({
		byAddress = true,
		address = null,
		lat = null,
		lng = null,
	} = {}) {
		this.locationsLoader.classList.add("is-visible");
		this.clearLocations();

		const args = true === byAddress ? [address] : [{ lat, lng }];
		const method =
			true === byAddress ? loadLocationsByAddress : loadLocationsByLatLng;

		const {
			data: { success, data },
		} = await method.apply(null, args);

		this.locationsLoader.classList.remove("is-visible");

		if (true === success) {
			if (data.locations.length) {
				for (const location of data.locations) {
					this.createLocationRadio(location);
				}

				if (1 === data.locations.length) {
					this.locationsWrap.querySelector("input").checked = true;
				}

				this.foundLocationsHeader.innerText = getLocationsHeading(
					data.locations.length
				);

				this.locationDiv.classList.add("is-visible");
				this.locationSearchField.locationsLoaded = true;
				this.locationSearchField.setCustomValidity("");

				this.validator.refresh();
			} else {
				this.noLocationsMessage.classList.add("is-visible");
			}

			return data;
		}
		throw new Error();
	}

	displayLocationsError(message) {
		this.locationsError.innerText = message;
		this.locationsError.classList.add("is-visible");
	}

	createLocationRadio(location) {
		const radio = createLocationRadio({
			location,
			context: "registration",
			mapLink: false,
			name: "favoriteStore",
			required: true,
		});

		this.locationsWrap.appendChild(radio);
	}

	clearLocations() {
		while (this.locationsWrap.firstChild) {
			this.locationsWrap.removeChild(this.locationsWrap.lastChild);
		}
	}

	setProcessingState(button, processing = true) {
		if (true === processing) {
			button.disabled = true;
			button.classList.add("is-loading");
		} else {
			button.disabled = false;
			button.classList.remove("is-loading");
		}
	}
}
